// @noflow
import { useLocalisation } from '@/context/localisation/localisation'
import { Dog, Meal } from '@/gql/graphql'
import { ACCOUNT_ROUTES } from '@/routes'
import { useQuery } from '@apollo/client'
import Container from '@material-ui/core/Container'
import { Grid } from '@mui/material'
import i18next from 'i18next'
import { upperFirst } from 'lodash'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { possessive } from '@/utils/StringHelper'
import { countryCodeToLocaleCurrency } from '@/utils/countryCodeHelper'
import { formatCurrencyWithDecimal } from '@/utils/currency'
import { getTimezoneOffset } from '@/utils/getTimezoneOffset'
import * as Sentry from '@/utils/sentry'

import BREAKPOINTS from '@/constants/Breakpoints'

import useSubscriptionResume from '@/hooks/subscriptionResume'
import useWindowSize from '@/hooks/useWindowSize'

import ButternutBoxSprite from 'assets/images/illustrations/loading-screens/meal-pouch-with-bowl-and-yellow-hearts-illustration--sprite.svg'

import segmentTrack from '@/components/analytics/Analytics'
import { Button } from '@/components/elements/atoms/Button'
import Card from '@/components/elements/atoms/Card/Card'
import { FixedBase } from '@/components/elements/atoms/FixedBase'
import FlatButton from '@/components/elements/atoms/FlatButton/FlatButton'
import Modal from '@/components/elements/atoms/Modal/Modal'
import Text from '@/components/elements/atoms/Text/Text'
import NotificationContent, {
  V2Variant
} from '@/components/elements/molecules/NotificationContent/NotificationContent'
import ProductThumb from '@/components/elements/molecules/ProductThumb/ProductThumb'
import SwiperSlider from '@/components/elements/molecules/SwiperSlider/SwiperSlider'
import { ErrorState } from '@/components/elements/organisms/ErrorState'
import LoadingScreen from '@/components/elements/organisms/LoadingScreen/LoadingScreen'
import SuccessOverlay from '@/components/elements/organisms/SuccessOverlay/SuccessOverlay'
import STYLES from '@/components/pages/EditPlanPage/EditPlanPage.module.sass'
import Calendar from '@/components/pages/TreatmentsPausedPage/components/PausedDatePicker/components/Calendar'
import Base from '@/components/templates/Base'

import { EDIT_PLAN_PAGE_QUERY } from './queries/queries'

type MealCard = {
  image: string
  alt: string
  quantity: number
}

const EditPlanPage = (): JSX.Element | null => {
  const namespace = 'account'
  const container = useRef<HTMLDivElement>(null)
  const { windowWidth } = useWindowSize()
  const isMobile = windowWidth < BREAKPOINTS.md
  const [resumeLoading, setResumeLoading] = useState(false)
  const navigate = useNavigate()
  const { format } = useLocalisation()
  const { handleResumeSubscription } = useSubscriptionResume({
    hideToast: true
  })
  const [pausedDeliveryDate, setPausedDeliveryDate] = useState<Date | null>()
  const [modalOpen, setModalOpen] = useState(false)

  const { data, error, loading } = useQuery(EDIT_PLAN_PAGE_QUERY, {
    onError: (error) =>
      Sentry.captureException('Error occurred in EDIT_PLAN_PAGE_QUERY', {
        extra: { error },
        tags: {
          product: Sentry.Product.Account,
          team: Sentry.Team.Retention
        }
      })
  })

  useEffect(() => {
    const { previewUncreatedFutureBox } = data?.user?.subscription || {}

    const nextDeliveryDate = previewUncreatedFutureBox?.isoDeliveryDate

    if (nextDeliveryDate && !resumeLoading) {
      setPausedDeliveryDate(nextDeliveryDate)
    }
  }, [data?.user?.subscription, resumeLoading])

  const toggleModal = useCallback(() => {
    setModalOpen((prev) => !prev)
  }, [])

  const handleSubmitDateChange = useCallback(
    (date) => {
      setPausedDeliveryDate(date)
      setModalOpen(false)
    },
    [setPausedDeliveryDate]
  )

  const navigateToDeliveryDate = useCallback((): void => setModalOpen(true), [])

  const navigateToDailyAmount = useCallback((): void => {
    navigate(ACCOUNT_ROUTES.servingSize)
  }, [navigate])

  const navigateToEditRecipes = useCallback((): void => {
    navigate(ACCOUNT_ROUTES.editRecipes)
  }, [navigate])

  const navigateToAccount = useCallback(() => {
    setTimeout((): void => {
      window.location.href = '/account?topCard=welcomeBack'
    }, 1000)
  }, [])

  const triggerError = useCallback((error, msg) => {
    Sentry.captureException(msg, {
      extra: { error },
      tags: {
        product: Sentry.Product.Account,
        team: Sentry.Team.Retention
      }
    })

    toast.error(
      <NotificationContent
        copy={{
          text: 'notification.resume_failed',
          namespace: 'limited_offer_paused_page'
        }}
      />,
      V2Variant.error()
    )
  }, [])

  const restartPreviousPlanClicked = useCallback(async (): Promise<void> => {
    segmentTrack('Edit Plan Page - Restart Your Plan clicked')
    setResumeLoading(true)
    try {
      await handleResumeSubscription({
        variables: {
          deliveryDate: pausedDeliveryDate
            ? getTimezoneOffset(new Date(pausedDeliveryDate))
            : null
        }
      })
    } catch (error) {
      triggerError(
        error,
        'Could not resume subscription from SUBSCRIPTION_RESUME_MUTATION'
      )
    } finally {
      setResumeLoading(false)
      navigateToAccount()
    }
  }, [
    handleResumeSubscription,
    pausedDeliveryDate,
    triggerError,
    navigateToAccount
  ])

  if (error) {
    return (
      <ErrorState
        error={{
          name: 'Error returning Edit Plan Page',
          message: error.message,
          apollo: error
        }}
      />
    )
  }

  if (loading || !data) {
    return (
      <LoadingScreen
        isOpen
        title={{
          text: 'loading_screen.title',
          namespace: 'shared'
        }}
      />
    )
  }

  const {
    user: {
      preferredLanguage,
      shippingCountryCode,
      subscription: { previewUncreatedFutureBox, status, meal },
      dogs
    }
  } = data

  const { plan } = previewUncreatedFutureBox || {}

  const filteredMeals: MealCard[] =
    !isNull(meal) && meal
      ? meal
          .map((mealItem: Meal) => ({
            image: mealItem.flavour.thumbnail.src,
            alt: mealItem.flavour.name,
            quantity: mealItem.quantity
          }))
          .filter((mealItem: Meal) => mealItem.quantity > 0)
      : undefined

  // Get locale and currency and format pricings
  const { locale, currency } = countryCodeToLocaleCurrency(
    shippingCountryCode,
    preferredLanguage
  )

  const dailyPriceFormatted = formatCurrencyWithDecimal(plan.pricePerDay, {
    locale: locale,
    currency: currency
  })

  // Success screen for restarting previous plan
  if (status === 'active' && !resumeLoading) {
    return (
      <SuccessOverlay
        sprite={ButternutBoxSprite}
        dataTestId="subscription-confirmation-overlay"
        typography={{
          namespace: 'treatments_paused_page',
          text: 'success',
          margin: false
        }}
      />
    )
  }

  return (
    <>
      <Base background="brandYellow100">
        <Container
          maxWidth={false}
          disableGutters
          className={STYLES.container}
          ref={container}
        >
          <div className={STYLES.cardsWrapper}>
            <Card padding={0} shadow mask>
              <div className={STYLES.cardTitle}>
                <Text
                  namespace={namespace}
                  text={`segmented_paused_page.edit_plan_screen.daily_amount_card.title`}
                  element="h3"
                  variant={isMobile ? 'display18' : 'display24'}
                  margin={false}
                />
              </div>
              <div className={STYLES.cardContent}>
                <Text
                  namespace={namespace}
                  text={`segmented_paused_page.edit_plan_screen.daily_amount_card.subtitle`}
                  variables={{
                    count: dogs.length
                  }}
                  margin={false}
                />
                <div className={STYLES.planDetails}>
                  <div className={STYLES.detailsRow}>
                    {dogs.map((dog: Dog) => (
                      <div className={STYLES.dogRow} key={dog.name}>
                        <Text
                          namespace={namespace}
                          text={`segmented_paused_page.edit_plan_screen.daily_amount_card.amount`}
                          variables={{
                            possessiveName: possessive(
                              upperFirst(dog.name),
                              i18next.language
                            )
                          }}
                          variant={'display16'}
                          margin={false}
                        />
                        <Text
                          namespace={namespace}
                          text={`segmented_paused_page.edit_plan_screen.daily_amount_card.amount_per_day`}
                          variables={{
                            dailyAmount: dog.planPortionInGrams
                          }}
                          margin={false}
                        />
                      </div>
                    ))}
                  </div>
                  <FlatButton
                    text={{
                      text: `segmented_paused_page.edit_plan_screen.daily_amount_card.flat_button`,
                      namespace,
                      variant: 'textRegular16'
                    }}
                    onClick={navigateToDailyAmount}
                    identifier="segmented_paused_page.daily_amount_card.change_amount"
                  />
                </div>
              </div>
            </Card>

            <Card padding={0} shadow mask>
              <div className={STYLES.cardTitle}>
                <Text
                  namespace={namespace}
                  text={`segmented_paused_page.edit_plan_screen.in_the_box_card.title`}
                  element="h3"
                  variant={isMobile ? 'display18' : 'display24'}
                  margin={false}
                />
              </div>
              <div className={STYLES.cardContent}>
                <Text
                  namespace={namespace}
                  text={`segmented_paused_page.edit_plan_screen.in_the_box_card.subtitle`}
                  variables={{
                    possessiveName: possessive(
                      upperFirst(dogs[0].name),
                      i18next.language
                    ),
                    count: dogs.length
                  }}
                  margin={false}
                />

                {!isUndefined(filteredMeals) && (
                  <>
                    <div className={STYLES.planDetails}>
                      <div className={STYLES.detailsRow}>
                        <Text
                          namespace={namespace}
                          text={`segmented_paused_page.edit_plan_screen.in_the_box_card.recipes`}
                          variant={'display16'}
                          margin={false}
                        />
                      </div>
                      <FlatButton
                        text={{
                          text: `segmented_paused_page.edit_plan_screen.in_the_box_card.change_recipes`,
                          namespace,
                          variant: 'textRegular16'
                        }}
                        onClick={navigateToEditRecipes}
                        identifier="segmented_paused_page.in_the_box_card.change_recipes"
                      />
                    </div>
                    <Grid
                      item
                      container
                      rowSpacing={0}
                      flexDirection="column"
                      paddingTop={0}
                    >
                      <Grid item className={STYLES.slider}>
                        <SwiperSlider
                          variant="brandBlue100"
                          paddingOverride={!isMobile ? 32 : 16}
                          arrows={false}
                        >
                          {filteredMeals.map((meal: MealCard) => (
                            <ProductThumb
                              image={meal.image}
                              alt={meal.alt}
                              key={meal.image}
                              background="transparent"
                            />
                          ))}
                        </SwiperSlider>
                      </Grid>
                    </Grid>
                  </>
                )}
              </div>
            </Card>

            <Card padding={0} shadow mask>
              <div className={STYLES.cardTitle}>
                <Text
                  namespace={namespace}
                  text={`segmented_paused_page.edit_plan_screen.delivery_card.title`}
                  element="h3"
                  variant={isMobile ? 'display18' : 'display24'}
                  margin={false}
                />
              </div>
              <div className={STYLES.cardContent}>
                <Text
                  namespace={namespace}
                  text={`segmented_paused_page.edit_plan_screen.delivery_card.subtitle`}
                  margin={false}
                />
                <div className={STYLES.planDetails}>
                  <div className={STYLES.detailsRow}>
                    <Text
                      namespace={namespace}
                      text={`segmented_paused_page.edit_plan_screen.delivery_card.date`}
                      variant={'display16'}
                      margin={false}
                    />
                    <Text
                      namespace={namespace}
                      text={
                        !isUndefined(format) && pausedDeliveryDate
                          ? format(pausedDeliveryDate, 'iiii do MMMM')
                          : ''
                      }
                      translate={false}
                      margin={false}
                    />
                  </div>
                  <FlatButton
                    text={{
                      text: `segmented_paused_page.edit_plan_screen.delivery_card.flat_button`,
                      namespace,
                      variant: 'textRegular16'
                    }}
                    onClick={navigateToDeliveryDate}
                    identifier="segmented_paused_page.delivery_card.change_date"
                  />
                </div>
              </div>
            </Card>

            <Card padding={0} mask variant="brandYellow200">
              <div className={STYLES.pricingCard}>
                <Text
                  namespace={namespace}
                  text={`segmented_paused_page.edit_plan_screen.pricing_card.price_per_day`}
                  variables={{
                    dailyPrice: dailyPriceFormatted
                  }}
                  variant={'display16'}
                  margin={false}
                  align="right"
                />
                <Text
                  namespace={namespace}
                  text={`plan_review.price_disclaimer`}
                  margin={false}
                  align="right"
                  colour="brandBlue400"
                />
              </div>
            </Card>
          </div>
          <FixedBase backgroundColor="brandWhite90" container={container}>
            <div className={STYLES.footerContent}>
              <Button
                identifier="edit_plan.update"
                onClick={restartPreviousPlanClicked}
                typography={{
                  text: `segmented_paused_page.edit_plan_screen.sticky_cta`,
                  namespace
                }}
                skeleton={{
                  isLoading: resumeLoading
                }}
                disabled={resumeLoading}
              />
            </div>
          </FixedBase>
        </Container>
      </Base>
      <Modal
        isModalOpen={modalOpen}
        setOpenModal={toggleModal}
        width={800}
        bottomSticky={windowWidth < BREAKPOINTS.md}
        textAlign="center"
        backgroundColour="brandYellow100"
      >
        <Text
          text="home.upcoming_boxes.edit_delivery_date_cta"
          namespace="account"
          variant="display24"
          align="center"
        />

        <Calendar
          userId={data.user.id}
          pausedDeliveryDate={new Date(pausedDeliveryDate || '')}
          handleSubmit={handleSubmitDateChange}
        />
      </Modal>
    </>
  )
}

export default EditPlanPage
